import { Camera, Check } from 'lucide-react';
import type { ReportDraftSection, ReportPhotoSelection } from '@officehub/shared';
import type { ReportWizardPhoto } from './types';
import { SurveyPhotoImage } from '../photo/SurveyPhotoImage';
import { CARD } from './ui';

interface Props {
  selections: ReportPhotoSelection[];
  photos: ReportWizardPhoto[];
  /** Photos in the survey that are not classified yet. */
  waitingCount?: number;
  sections: ReportDraftSection[];
  onToggle(photoId: string): void;
  onAssign(photoId: string, sectionId: string | undefined): void;
}

const label = (p: ReportWizardPhoto) => (p.category ?? 'photo').replace(/_/g, ' ');

export function PhotoCard({ selections, photos, waitingCount = 0, sections, onToggle, onAssign }: Props) {
  const byId = new Map(photos.map((p) => [p.photoId, p]));
  const shown = [...selections]
    .filter((s) => byId.has(s.photoId))
    .sort((a, b) => (a.included !== b.included ? (a.included ? -1 : 1) : b.aiScore - a.aiScore));
  const selected = selections.filter((s) => s.included).length;
  const orderedSections = [...sections].sort((a, b) => a.order - b.order);

  if (shown.length === 0) {
    return (
      <div className={`${CARD} text-center`}>
        <Camera size={32} className="mx-auto mb-2 text-slate-200" />
        <p className="text-sm text-slate-500">
          {waitingCount > 0
            ? `${waitingCount} ${waitingCount === 1 ? 'photo is' : 'photos are'} not classified yet, so none are in the report`
            : 'No photos in this survey'}
        </p>
      </div>
    );
  }
  return (
    <div className={CARD}>
      <h3 className="mb-1 text-sm font-bold text-slate-900">Photos for Report</h3>
      <p className="mb-3 text-xs text-slate-500">
        Tap to include or exclude. <span className="font-semibold text-[#007ACC]">{selected} of {selections.length} selected</span>
      </p>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {shown.map((sel) => {
          const photo = byId.get(sel.photoId)!;
          return (
            <div key={sel.photoId} className="space-y-1.5">
              <button
                type="button"
                aria-pressed={sel.included}
                aria-label={`Include photo: ${label(photo)}`}
                onClick={() => onToggle(sel.photoId)}
                className={`relative block aspect-square min-h-[44px] w-full overflow-hidden rounded-xl border-2 ${sel.included ? 'border-[#007ACC]' : 'border-slate-200 opacity-50'}`}
              >
                <SurveyPhotoImage url={photo.url} storageRef={photo.storageRef} alt={photo.description ?? ''} className="h-full w-full object-cover" />
                <span className={`absolute right-1 top-1 rounded px-1 py-0.5 text-[10px] font-bold text-white ${sel.aiScore >= 80 ? 'bg-emerald-500' : sel.aiScore >= 60 ? 'bg-amber-500' : 'bg-slate-500'}`}>
                  {sel.aiScore}
                </span>
                {sel.included && (
                  <span className="absolute left-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-[#007ACC] text-white"><Check size={12} /></span>
                )}
                <span className="absolute bottom-0 left-0 right-0 truncate bg-black/50 px-1.5 py-0.5 text-[10px] font-semibold capitalize text-white">{label(photo)}</span>
              </button>
              {sel.aiReason && <p className="text-[11px] leading-snug text-slate-500">{sel.aiReason}</p>}
              <select
                aria-label="Section for this photo"
                value={sel.sectionId ?? ''}
                onChange={(e) => onAssign(sel.photoId, e.target.value || undefined)}
                className="min-h-[44px] w-full rounded-lg border border-slate-300 bg-white px-2 text-xs text-slate-700"
              >
                <option value="">Any section</option>
                {orderedSections.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
              </select>
            </div>
          );
        })}
      </div>
    </div>
  );
}
